import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'

import {
  ALLOCATION_BOARD_POLL_INTERVAL_MS,
  QUERY_GC_TIME_MS,
} from '@/config/constants'
import { useIsCareforceOrgAdmin } from '@/hooks/useIsCareforceOrgAdmin'
import { phoneLineKeys } from '@/lib/queryKeys'

import { fetchAllocationBoard } from '../api'
import type { DashboardBoard } from '../types'

/**
 * Single polled data source for the combined outbound dashboard (line grid + map,
 * usage ribbon, backlog table). Super-user-only (the endpoint 403s otherwise) and
 * global / cross-tenant. The endpoint returns the whole-page `DashboardBoard`
 * (`pool` + `lines[]` + `reserved_by_duty[]` + `duties[]`) in one snapshot.
 *
 * Gated on `useIsCareforceOrgAdmin`, not `useIsSuperUser`: helpcare re-gates the
 * board on the active-org claim, so a Careforce admin viewing a customer org
 * would 403 every 2s. `CareforceOrgNotice` explains that case on the page.
 */
export function useAllocationBoardQuery() {
  const { getToken, userId } = useAuth()
  const isCareforceOrgAdmin = useIsCareforceOrgAdmin()

  return useQuery({
    queryKey: phoneLineKeys.allocationBoard(userId),
    queryFn: async (): Promise<DashboardBoard> => {
      const token = await getToken()
      if (!token) {
        throw new Error('No authentication token available')
      }
      return fetchAllocationBoard(token)
    },
    enabled: isCareforceOrgAdmin,
    // staleTime matches the interval so data isn't held fresh past a poll.
    staleTime: ALLOCATION_BOARD_POLL_INTERVAL_MS,
    refetchInterval: ALLOCATION_BOARD_POLL_INTERVAL_MS,
    gcTime: QUERY_GC_TIME_MS,
    refetchOnWindowFocus: false,
  })
}
